import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { gsap } from 'gsap';
import { Shield, Loader2, CheckCircle, User } from 'lucide-react';

const SetUsernamePage = () => {
  const { user } = useAuth(); 
  const [username, setUsername] = useState('');
  const [initialUsername, setInitialUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [gridDimensions, setGridDimensions] = useState({ rows: 0, cols: 0 });
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    const updateGridDimensions = () => {
      setGridDimensions({
        rows: Math.ceil(window.innerHeight / 40),
        cols: Math.ceil(window.innerWidth / 40),
      });
    };
    updateGridDimensions();
    window.addEventListener('resize', updateGridDimensions);
    return () => window.removeEventListener('resize', updateGridDimensions);
  }, []);

  useEffect(() => {
    const tl = gsap.timeline();
    gsap.set([leftPanelRef.current, rightPanelRef.current], { opacity: 0, x: -50 });
    gsap.set(rightPanelRef.current, { x: 50 });
    tl.to([leftPanelRef.current, rightPanelRef.current], {
      opacity: 1,
      x: 0,
      duration: 0.8,
      ease: 'power3.out',
      stagger: 0.2,
    });
  }, []);

  // Preload existing username if present
  useEffect(() => {
    const loadExisting = async () => {
      try {
        if (!user?.id) return;
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username')
          .eq('id', user.id)
          .maybeSingle();

        if (!error && data?.username) {
          setUsername(data.username);
          setInitialUsername(data.username);
        }
      } catch (e) {
        // ignore
      }
    };
    loadExisting();
  }, [user?.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmed = username.trim();
    if (!trimmed) {
      setError('Username is required');
      return;
    }
    if (!/^[a-zA-Z0-9._-]{3,20}$/.test(trimmed)) {
      setError('Use 3–20 chars: letters, numbers, dot, underscore, hyphen.');
      return;
    }

    setLoading(true);
    try {
      const { data: existing, error: checkError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', trimmed)
        .maybeSingle();

      if (checkError) throw checkError;
      // Allow if the only match is the current user
      if (existing && existing.id !== user.id) {
        setError('Username is already taken');
        return;
      }

      if (trimmed !== initialUsername) {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ username: trimmed })
          .eq('id', user.id);
        if (updateError) throw updateError;
      }

      setSuccess('Username set successfully!');
      try {
        const key = user?.id ? `username_setup_done_${user.id}` : null;
        if (key) localStorage.setItem(key, '1');
      } catch (_) {}
      // small delay for UX, then go to dashboard
      setTimeout(() => navigate('/dashboard'), 600);
    } catch (err) {
      console.error(err);
      setError('Failed to set username. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center relative overflow-hidden"
    >
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(circle at 50% 50%, 
                rgba(184, 70, 14, 0.15) 0%, 
                rgba(184, 70, 14, 0.08) 40%, 
                rgba(184, 70, 14, 0.03) 70%,
                rgba(184, 70, 14, 0) 100%
              )
            `,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            maskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
            WebkitMaskImage: `
              radial-gradient(circle at 50% 50%, 
                rgba(255,255,255,1) 0%, 
                rgba(255,255,255,0.8) 30%, 
                rgba(255,255,255,0.3) 60%,
                rgba(255,255,255,0.1) 80%,
                rgba(255,255,255,0) 100%
              )
            `,
          }}
        />
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: gridDimensions.rows }).map((_, row) =>
            Array.from({ length: gridDimensions.cols }).map((_, col) => (
              <div
                key={`${row}-${col}`}
                className="absolute w-10 h-10 pointer-events-auto transition-all duration-300"
                style={{ left: `${col * 40}px`, top: `${row * 40}px`, animation: 'none' }}
                onMouseEnter={(e) => {
                  gsap.fromTo(
                    e.target,
                    { backgroundColor: 'rgba(184, 70, 14, 0)', boxShadow: '0 0 0px rgba(184, 70, 14, 0)', scale: 1 },
                    {
                      backgroundColor: 'rgba(184, 70, 14, 0.3)',
                      boxShadow:
                        '0 0 25px rgba(184, 70, 14, 0.8), inset 0 0 15px rgba(184, 70, 14, 0.3)',
                      scale: 1.05,
                      duration: 0.2,
                      ease: 'power2.out',
                    }
                  );
                }}
                onMouseLeave={(e) => {
                  gsap.to(e.target, {
                    backgroundColor: 'rgba(184, 70, 14, 0)',
                    boxShadow: '0 0 0px rgba(184, 70, 14, 0)',
                    scale: 1,
                    duration: 0.4,
                    ease: 'power2.out',
                  });
                }}
                data-row={row}
                data-col={col}
              />
            ))
          )}
        </div>
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl lg:rounded-3xl shadow-2xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            <div
              ref={leftPanelRef}
              className="w-full lg:w-1/2 p-4 sm:p-6 lg:p-8 flex items-center justify-center order-2 lg:order-1"
            >
              <div className="w-full max-w-sm">
                <div ref={formRef}>
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 lg:w-14 lg:h-14 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] rounded-2xl flex items-center justify-center shadow-lg">
                      <User className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                    </div>
        </div>
        
                  <div className="text-center mb-6">
                    <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">Choose your username</h1>
                    <p className="text-sm lg:text-base text-gray-300">This will be visible across the dashboard</p>
          </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    {success && (
                      <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2.5 rounded-xl text-sm flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                        {success}
                      </div>
                    )}

          {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-xl text-sm">
              {error}
            </div>
          )}

          <div>
                      <label className="block text-gray-300 text-sm font-semibold mb-1.5">Username</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className="w-full bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl px-4 py-2.5 lg:py-3 text-sm lg:text-base text-white placeholder-gray-400 focus:outline-none focus:border-[#B8460E] focus:ring-2 focus:ring-[#B8460E]/20 transition-all duration-200"
                          placeholder="e.g., jack247"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

            <button
              type="submit"
              disabled={loading}
                      className="w-full bg-gradient-to-r from-[#B8460E] via-[#8B3A0E] to-[#B8460E] hover:from-[#D4541A] hover:via-[#A0421A] hover:to-[#D4541A] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 lg:py-3 px-6 rounded-xl transition-all duration-200 font-semibold flex items-center justify-center text-sm lg:text-base shadow-lg hover:shadow-xl relative overflow-hidden group"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 lg:w-5 lg:h-5 mr-2 animate-spin" />
                          Setting Username...
                        </>
                      ) : (
                        'Set Username'
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div
              ref={rightPanelRef}
              className="w-full lg:w-1/2 bg-gradient-to-br from-[#B8460E] via-[#8B3A0E] to-[#B8460E] p-4 sm:p-6 lg:p-8 flex items-center justify-center relative overflow-hidden order-1 lg:order-2"
            >
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-white text-center max-w-md">
                <div className="mb-4 lg:mb-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 lg:w-16 lg:h-16 bg-white/20 rounded-3xl mb-3 lg:mb-4 backdrop-blur-sm">
                    <Shield className="w-7 h-7 lg:w-8 lg:h-8" />
                  </div>
                  <h2 className="text-xl lg:text-3xl font-bold mb-3 lg:mb-4 leading-tight">Why a username?</h2>
                  <p className="text-sm lg:text-lg text-orange-100 leading-relaxed mb-4 lg:mb-6">
                    Your username identifies you across performance analytics, payouts, and chat. Pick something unique.
                  </p>
                </div>

                <div className="space-y-2 lg:space-y-3 mb-4 lg:mb-6 text-left">
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <span className="text-sm text-orange-100">Use 3–20 characters</span>
                  </div>
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <span className="text-sm text-orange-100">Letters, numbers, dot, underscore, hyphen</span>
                  </div>
                  <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-xl p-2.5 lg:p-3">
                    <span className="text-sm text-orange-100">Avoid personal info</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetUsernamePage;